import { useState } from 'react';
import { Alert, App, Button, Checkbox, Spin, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileStack } from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router';
import {
  ARRANGEMENT_TYPES,
  readableDate,
  type CreateTemplateRunDto,
  type TemplatePlanDto,
  type TemplatePlanItemDto,
  type TemplateRunDto,
} from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { api, ApiError } from '@/lib/api';
import { TEMPLATE_DECISION_CLASS_LABEL } from '@/lib/labels';
import { queryKeys } from '@/lib/queryKeys';
import { orderStatusWord } from '@/lib/statusPresentation';
import { RunProgress } from '../components/RunProgress';
import { CREATE_COPY, DECISIONS_COPY } from '../copy';
import { idsFromParam } from '../templateQueries';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B §4c/§4d] PLAN, THEN APPROVE, THEN CREATE — `/templates/:setKey/create?year=&t=` (exit criteria
 *  10 and 11).
 *
 *  ⚠️ NOTHING IS WRITTEN UNTIL "APPROVE". The plan is a POST because its input is a list, and the server
 *  writes nothing for it; this page reads it once, keyed on (set, year, templates), and never re-asks on
 *  its own — a plan worked out again behind the reader's back would change what they are approving. It
 *  is worked out again only by `Work it out again`, by a discard-first, or after a stale approval.
 *
 *  ⚠️ APPROVAL SENDS THE PLAN'S DIGEST. The server works the plan out again and answers 409 if anything
 *  moved since this screen read it; the page then re-reads the plan and says so, and the operator
 *  approves what is now on screen. Never a silent retry.
 *
 *  ⚠️ "ALREADY PRODUCED" NEEDS AN ACKNOWLEDGEMENT PER TEMPLATE — or the existing draft discarded first
 *  (the ordinary Discard, restorable for thirty days). A published order is never discarded from here:
 *  it links to its own page.
 *
 *  ⚠️ AFTER APPROVAL THE PAGE BECOMES THE RUN (`?run=<id>`), so a closed tab is a paused run and the
 *  same link resumes it.
 *
 *  ⚠️ [TEMPLATE-FIND] A DUTY POINT IS NAMED IN THE SERVER'S WORDS, NEVER BY ITS KEY: "People left out"
 *  prints `dutyPointLabel` (`(b)`, `table 1, row 2`) and keeps `dutyPoint` (`g1.d2`) for the React key
 *  alone; round the clock's left-out duty points and every resolution's label are the server's own
 *  wording too, the Decisions page's labels. Those words carry commas, so a list of them joins on `; `.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
const datesWords = (dates: readonly string[]): string => {
  const [first] = dates;
  const last = dates.at(-1);
  if (first === undefined || last === undefined) return '—';
  if (dates.length <= 3) return dates.map((date) => readableDate(date) ?? date).join(', ');
  return `${readableDate(first) ?? first} – ${readableDate(last) ?? last} (${String(dates.length)} dates)`;
};

const TemplateCreatePage = (): React.ReactElement => {
  const { setKey = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const runId = searchParams.get('run');
  const templateIds = idsFromParam(searchParams.get('t'));
  const year = Number(searchParams.get('year'));
  const [acknowledged, setAcknowledged] = useState<string[]>([]);
  const [stale, setStale] = useState(false);

  const planKey = queryKeys.templates.plan(setKey, templateIds, year);
  const plan = useQuery({
    queryKey: planKey,
    queryFn: ({ signal }) =>
      api.post<TemplatePlanDto>(`/templates/sets/${setKey}/plan`, { templateIds, year }, { signal }),
    enabled: runId === null && templateIds.length > 0 && Number.isInteger(year) && year > 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const discardFirst = useMutation({
    mutationFn: (arrangementId: string) => api.post(`/arrangements/${arrangementId}/archive`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.all });
      void plan.refetch();
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  const approve = useMutation({
    mutationFn: (body: CreateTemplateRunDto) => api.post<TemplateRunDto>(`/templates/sets/${setKey}/runs`, body),
    onSuccess: (run) => {
      queryClient.setQueryData(queryKeys.templates.run(run.id), run);
      setSearchParams(
        (current) => {
          current.set('run', run.id);
          return current;
        },
        { replace: true },
      );
    },
    onError: (error: ApiError) => {
      if (error instanceof ApiError && error.status === 409) {
        setStale(true);
        void plan.refetch();
        return;
      }
      void message.error(error.message);
    },
  });

  const header = (
    <PageHeader
      icon={FileStack}
      parent={{ label: DECISIONS_COPY.parent, to: `/templates/${setKey}` }}
      title={CREATE_COPY.title}
    />
  );

  if (runId !== null) {
    return (
      <div className="flex flex-col gap-4" data-testid="template-create">
        {header}
        <RunProgress runId={runId} />
      </div>
    );
  }

  if (templateIds.length === 0) {
    return (
      <div className="flex flex-col gap-4" data-testid="template-create">
        {header}
        <EmptyState title={CREATE_COPY.nothingSelected} />
      </div>
    );
  }

  if (plan.isPending || plan.isFetching) {
    return (
      <div className="flex flex-col gap-4" data-testid="template-create">
        {header}
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
          <Spin /> {CREATE_COPY.planning}
        </div>
      </div>
    );
  }

  if (!plan.data) {
    return (
      <div className="flex flex-col gap-4" data-testid="template-create">
        {header}
        <Alert type="error" showIcon message={plan.error?.message ?? CREATE_COPY.nothingToCreate} />
      </div>
    );
  }

  const data = plan.data;
  /* A template still holding a live order needs an acknowledgement; one whose orders are all discarded does not. */
  const needingAck = data.produced.filter((entry) => entry.orders.some((order) => order.archivedAt === null));
  const unacknowledged = needingAck.filter((entry) => !acknowledged.includes(entry.templateId));
  const blockedApproval =
    data.items.length === 0
      ? CREATE_COPY.nothingToCreate
      : unacknowledged.length > 0
        ? CREATE_COPY.needsAcknowledgement
        : null;

  const confirmApprove = (): void => {
    modal.confirm({
      title: CREATE_COPY.approveConfirmTitle(data.totals.orders),
      content: <span className="text-sm">{CREATE_COPY.approveConfirmBody}</span>,
      okText: CREATE_COPY.approve,
      onOk: () => {
        setStale(false);
        approve.mutate({
          kind: 'CREATE',
          templateIds,
          year,
          digest: data.digest,
          acknowledgeProduced: needingAck.map((entry) => entry.templateId).filter((id) => acknowledged.includes(id)),
        });
      },
    });
  };

  const itemColumns: ColumnsType<TemplatePlanItemDto> = [
    {
      key: 'title',
      title: '',
      render: (_value, item) => (
        <span className="flex max-w-[24rem] flex-col">
          <span>{item.title}</span>
          <span className="text-xs text-muted-foreground">
            {[item.templateKey, CREATE_COPY.part(item.part, item.parts)].filter((part) => part !== '').join(' · ')}
          </span>
        </span>
      ),
    },
    {
      key: 'when',
      title: '',
      render: (_value, item) => (
        <span className="flex flex-col">
          <span>{datesWords(item.dates)}</span>
          <span className="text-xs text-muted-foreground">{item.times}</span>
        </span>
      ),
    },
    {
      key: 'size',
      title: '',
      render: (_value, item) => (
        <span className="text-xs text-muted-foreground">
          {CREATE_COPY.itemSize(item.dutyPointCount, item.deploymentCount, item.slotCount)}
        </span>
      ),
    },
    {
      key: 'people',
      title: '',
      render: (_value, item) => (
        <span className="flex flex-col text-xs">
          {item.people.map((row) => (
            <span key={`${row.column}|${row.source}`}>
              {row.column} · {row.source} · {row.count}
            </span>
          ))}
          <span className="text-muted-foreground">{CREATE_COPY.inChargeEmpty(item.inChargeRoles)}</span>
        </span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4" data-testid="template-create">
      {header}

      <section className="flex flex-col gap-1 rounded-lg border border-border bg-card px-4 py-3 text-sm">
        <span className="font-medium text-heading" data-testid="plan-totals">
          {CREATE_COPY.totals(data.totals.orders, data.totals.dutyPoints, data.totals.deployments, data.totals.slots)}
        </span>
        <span className="text-muted-foreground">
          {CREATE_COPY.forYear(ARRANGEMENT_TYPES[data.type].label, data.year, data.paDate)}
        </span>
        <span>{CREATE_COPY.nothingWritten}</span>
        <span className="text-xs text-muted-foreground">{CREATE_COPY.draftAge}</span>
      </section>

      {stale ? <Alert type="warning" showIcon data-testid="plan-stale" message={CREATE_COPY.stale} /> : null}

      <h2 className="text-sm font-medium text-heading">{CREATE_COPY.itemsTitle}</h2>
      <Table
        rowKey={(item) => item.key}
        columns={itemColumns}
        dataSource={data.items}
        showHeader={false}
        size="small"
        pagination={data.items.length > 50 ? { pageSize: 50 } : false}
        scroll={{ x: 'max-content' }}
        data-testid="plan-items"
      />

      {data.produced.length > 0 ? (
        <section className="flex flex-col gap-2 text-sm" data-testid="plan-produced">
          <h2 className="font-medium text-heading">{CREATE_COPY.producedTitle}</h2>
          <p className="text-muted-foreground">{CREATE_COPY.producedBody}</p>
          {data.produced.map((entry) => (
            <div key={entry.templateId} className="flex flex-col gap-1 rounded-lg border border-border px-3 py-2">
              <span className="font-mono">{entry.templateKey}</span>
              {entry.orders.map((order) => (
                <span key={order.arrangementId} className="flex flex-wrap items-center gap-2">
                  <Link to={`/arrangements/${order.arrangementId}`} className="text-link">
                    {order.paNumber ?? order.reference}
                  </Link>
                  <span className="text-muted-foreground">{orderStatusWord(order).label}</span>
                  {order.status === 'DRAFT' && order.archivedAt === null ? (
                    <Button
                      size="small"
                      data-testid={`discard-first-${order.arrangementId}`}
                      loading={discardFirst.isPending && discardFirst.variables === order.arrangementId}
                      onClick={() => {
                        modal.confirm({
                          title: CREATE_COPY.discardConfirmTitle,
                          content: <span className="text-sm">{CREATE_COPY.discardConfirmBody}</span>,
                          okText: CREATE_COPY.discardFirst,
                          okButtonProps: { danger: true },
                          onOk: () => {
                            discardFirst.mutate(order.arrangementId);
                          },
                        });
                      }}
                    >
                      {CREATE_COPY.discardFirst}
                    </Button>
                  ) : null}
                </span>
              ))}
              {needingAck.some((each) => each.templateId === entry.templateId) ? (
                <Checkbox
                  checked={acknowledged.includes(entry.templateId)}
                  data-testid={`acknowledge-${entry.templateKey}`}
                  onChange={(event) => {
                    setAcknowledged((current) =>
                      event.target.checked
                        ? [...current, entry.templateId]
                        : current.filter((id) => id !== entry.templateId),
                    );
                  }}
                >
                  {CREATE_COPY.acknowledge}
                </Checkbox>
              ) : null}
            </div>
          ))}
        </section>
      ) : null}

      {data.blocked.length > 0 ? (
        <section className="flex flex-col gap-1 text-sm" data-testid="plan-blocked">
          <h2 className="font-medium text-heading">{CREATE_COPY.blockedTitle}</h2>
          {data.blocked.map((entry) => (
            <div key={entry.templateId} className="flex flex-col">
              <span>
                <span className="font-mono">{entry.templateKey}</span> · {entry.title}
              </span>
              {entry.reasons.map((reason) => (
                <span key={reason} className="text-muted-foreground">
                  {reason}
                </span>
              ))}
            </div>
          ))}
        </section>
      ) : null}

      {data.roundTheClock.length > 0 ? (
        <section className="flex flex-col gap-1 text-sm" data-testid="plan-round-the-clock">
          <h2 className="font-medium text-heading">{CREATE_COPY.roundTheClockTitle}</h2>
          {data.roundTheClock.map((entry) => (
            <span key={entry.templateId}>
              <span className="font-mono">{entry.templateKey}</span> · {entry.title} —{' '}
              {entry.wholeTemplate ? CREATE_COPY.wholeTemplate : CREATE_COPY.excluded(entry.excludedDutyPoints)}
            </span>
          ))}
        </section>
      ) : null}

      {data.dropped.length > 0 ? (
        <section className="flex flex-col gap-1 text-sm" data-testid="plan-dropped">
          <h2 className="font-medium text-heading">{CREATE_COPY.droppedTitle}</h2>
          {data.dropped.map((row, index) => (
            <span
              key={`${row.templateKey}-${row.dutyPoint}-${row.column}-${String(index)}`}
              className="text-muted-foreground"
            >
              {row.templateKey} · {row.dutyPointLabel} · {row.column}
              {row.source ? ` (${row.source})` : ''} · {row.n} — {CREATE_COPY.droppedWhy[row.why]}
            </span>
          ))}
        </section>
      ) : null}

      {data.resolutions.length > 0 ? (
        <details className="rounded-lg border border-border px-3 py-2 text-sm" data-testid="plan-resolutions">
          <summary className="cursor-pointer">{CREATE_COPY.resolutionsTitle}</summary>
          <ul className="mt-2 flex flex-col gap-0.5">
            {data.resolutions.map((row) => (
              <li key={`${row.class}|${row.code}`} className="text-muted-foreground">
                {TEMPLATE_DECISION_CLASS_LABEL[row.class]} · {row.label} —{' '}
                {CREATE_COPY.resolutionBy(row.byRule, row.decidedBy)}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="primary"
          data-testid="plan-approve"
          disabled={blockedApproval !== null}
          loading={approve.isPending}
          onClick={confirmApprove}
        >
          {CREATE_COPY.approve}
        </Button>
        <Button
          onClick={() => {
            setStale(false);
            void plan.refetch();
          }}
        >
          {CREATE_COPY.replan}
        </Button>
        {blockedApproval !== null ? (
          <span className="text-sm text-muted-foreground" data-testid="plan-approve-blocked">
            {blockedApproval}
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default TemplateCreatePage;
